import * as Styled from './PunchupResponsesTable.styles';

import {
	PAID_COLUMN_HEADINGS,
	PAID_COLUMN_MAX_WIDTH,
	PAID_COLUMN_MIN_WIDTH,
	PAID_COLUMN_MIN_WIDTH_HEADERS,
	REGISTRATION_COLUMN_HEADINGS,
	REGISTRATION_COLUMN_MAX_WIDTH,
	REGISTRATION_COLUMN_MIN_WIDTH,
	REGISTRATION_COLUMN_MIN_WIDTH_HEADERS,
	RESPONSES_INDEX,
} from './constants';
import { FC, useEffect, useState } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FlexContainer } from 'features/common/components/FlexContainer';
import { OUTLINED } from 'features/common/components/Button/constants';
import { PunchupTableBody } from './PunchupTableBody';
import { ResponsesProps, SortField } from 'features/CustomFieldsResponses/types';
import { Table } from 'features/common/components/Table';
import { buildGridColumns, getCellIndex } from './utils';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { IdParamsType } from 'utils/types';
import { usePinnedActions } from 'features/CustomFieldsResponses/hooks/usePinnedActions';
import { SortableColumnHeader } from 'features/common/components/Table/SortableColumnHeader';
import { ScrollableTableWrapper, useScrollableTable } from 'features/common/components/Table/ScrollableTableWrapper';

const ACTION_BUTTON_SIZE = 32;
const ACTION_GAP = 2;
const ACTION_HORIZONTAL_PADDING = 8;
const MIN_ACTION_WIDTH = 60; // header always renders 2 scroll arrows (24px each + 4px gap + 8px padding)

export const PunchupResponsesTable: FC<ResponsesProps> = ({
	responsesQueryObject,
	isAttendeesList,
	eventMode,
	sortBy,
	order,
	onSort,
}) => {
	const { t, i18n } = useTranslation();
	const { id: eventId } = useParams<IdParamsType>();
	const { pinnedIds } = usePinnedActions(eventId);
	const actionWidth = Math.max(
		MIN_ACTION_WIDTH,
		ACTION_BUTTON_SIZE + ACTION_HORIZONTAL_PADDING + pinnedIds.size * (ACTION_BUTTON_SIZE + ACTION_GAP)
	);
	const { fetchNextPage, hasNextPage, data } = responsesQueryObject;

	const [headings, setHeadings] = useState<string[]>([]);
	const [questions, setQuestions] = useState<string[]>([]);
	const [showDownloadQR, setShowDownloadQR] = useState(false);

	const customHeadings = eventMode === 'normal' ? PAID_COLUMN_HEADINGS : REGISTRATION_COLUMN_HEADINGS;
	const COLUMN_MIN_WIDTH_HEADERS =
		eventMode === 'normal' ? PAID_COLUMN_MIN_WIDTH_HEADERS : REGISTRATION_COLUMN_MIN_WIDTH_HEADERS;
	const COLUMN_MIN_WIDTH = eventMode === 'normal' ? PAID_COLUMN_MIN_WIDTH : REGISTRATION_COLUMN_MIN_WIDTH;
	const COLUMN_MAX_WIDTH = eventMode === 'normal' ? PAID_COLUMN_MAX_WIDTH : REGISTRATION_COLUMN_MAX_WIDTH;

	useEffect(() => {
		const tempHeadings = [...customHeadings];
		if (!data || data.pages.length === 0) {
			setShowDownloadQR(false);
			setHeadings(tempHeadings);
			return;
		}

		const questions: string[] = [];
		data.pages.forEach((page) => {
			page.forEach((response) => {
				response.responses.forEach((response) => {
					if (!['ATTENDEE_NAME', 'ATTENDEE_EMAIL'].includes(response.custom_field.name))
						questions.push(response.custom_field.name);
				});
			});
		});

		const uniqueQuestions = Array.from(new Set(questions));
		setQuestions(uniqueQuestions);

		if (isAttendeesList) {
			tempHeadings.push(...uniqueQuestions);
		} else {
			tempHeadings.splice(RESPONSES_INDEX, 0, ...uniqueQuestions);
		}
		setHeadings(tempHeadings);

		if (data.pages.some((page) => page.some((response) => response.can_download_qr))) {
			setShowDownloadQR(true);
		}
	}, [data?.pages]);

	const gridColumns = buildGridColumns(headings, customHeadings, eventMode, actionWidth);

	// Mapping between heading translation keys and sort field names
	const getSortField = (heading: string): SortField | null => {
		const sortFieldMap: Record<string, SortField> = {
			'customFields-responses-table-orderId': 'order_id',
			'customFields-responses-table-email': 'email',
			'customFields-responses-table-fullName': 'full_name',
			'customFields-responses-table-amount': 'amount',
			'customFields-responses-table-date': 'date',
			'customFields-responses-table-status': 'status',
			'customFields-responses-table-scanned': 'scan_count',
		};
		return sortFieldMap[heading] || null;
	};

	return (
		<>
			<ScrollableTableWrapper>
				<Table.TableContainer>
					<Table>
						<thead>
							<Styled.HeaderRow gridColumns={gridColumns} textAlign="center">
								{headings.map((heading, i) => {
									const index = getCellIndex(i, headings, RESPONSES_INDEX, customHeadings);
									const sortField = getSortField(heading);

									return (
										<Table.Cell
											key={heading}
											minWidth={COLUMN_MIN_WIDTH_HEADERS[index]}
											maxWidth={COLUMN_MAX_WIDTH[index]}
										>
											{sortField ? (
												<SortableColumnHeader
													label={i18n.exists(heading) ? t(heading) : heading}
													sortKey={sortField}
													currentSortBy={sortBy}
													currentOrder={order}
													onSort={onSort}
													align="center"
													variant="punchup"
												/>
											) : (
												<Styled.ColumnHeader>{i18n.exists(heading) ? t(heading) : heading}</Styled.ColumnHeader>
											)}
										</Table.Cell>
									);
								})}

								<Styled.StickyActionHeaderCell minWidth={actionWidth} maxWidth={actionWidth} textAlign="center">
									<ScrollArrows />
								</Styled.StickyActionHeaderCell>

								{isAttendeesList && showDownloadQR && (
									<Table.Cell minWidth={COLUMN_MIN_WIDTH[9]} maxWidth={COLUMN_MAX_WIDTH[9]} textAlign="center">
										<Table.ColumnHeader>{t('customFields-responses-ticketQR')}</Table.ColumnHeader>
									</Table.Cell>
								)}
							</Styled.HeaderRow>
						</thead>

						<PunchupTableBody
							responsesQueryObject={responsesQueryObject}
							columnsN={headings.length}
							gridColumns={gridColumns}
							questions={questions}
							isAttendeesList={isAttendeesList}
							showDownloadQR={showDownloadQR}
							eventMode={eventMode}
							actionWidth={actionWidth}
						/>
					</Table>
				</Table.TableContainer>
			</ScrollableTableWrapper>
			<FlexContainer justifyContent="center" margin="32px 0">
				<Table.Button
					category={OUTLINED}
					text={t('button-load-more')}
					onClick={() => fetchNextPage()}
					disabled={!hasNextPage}
					type="button"
				/>
			</FlexContainer>
		</>
	);
};

const ScrollArrows: FC = () => {
	const { showLeft, showRight, scrollLeft, scrollRight } = useScrollableTable();
	if (!showLeft && !showRight) return null;
	return (
		<Styled.ScrollControls>
			<Styled.ScrollChevronButton type="button" onClick={scrollLeft} disabled={!showLeft} aria-label="Scroll left">
				<ChevronLeft />
			</Styled.ScrollChevronButton>
			<Styled.ScrollChevronButton type="button" onClick={scrollRight} disabled={!showRight} aria-label="Scroll right">
				<ChevronRight />
			</Styled.ScrollChevronButton>
		</Styled.ScrollControls>
	);
};
